import React, { useState, useEffect, useContext } from 'react';
import { useParams } from "react-router-dom";
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { Box, Button, Collapse, Typography, useTheme, Grid, Divider, 
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Paper, IconButton } from "@mui/material";
import {
  GridRowModes,
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridEditInputCell,
} from '@mui/x-data-grid';
import DataGridCustomToolbar from '../../components/DataGridCustomToolbar';
import Header from "../../components/Header";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
// import { AssessmentContext } from '../SecurityGroup1';



const DefaultCriticalAssets = [
        {
          index: 0,
            assetType: 'applicationServer',
            assetName: 'Application Server',
            description: 'Application servers are used across many different sectors to host various diverse software applications necessary to supporting the ICS. Example functions can include data analytics and reporting, alarm management, and the management/coordination of different control servers. The application server typically runs on a modern server operating system (e.g., MS Windows Server).',
            example: 'Windows Server 2022, Red Hat Enterprise Linux 9, or other Linux server'
        },
        {
          index: 1,
            assetType: 'controlServer',
            assetName:'Control Server',
            description: 'Control servers are typically a software platform that runs on a modern server operating system (e.g., MS Windows Server). The server typically uses one or more automation protocols (e.g., Modbus, DNP3) to communicate with the various low-level control devices such as Remote Terminal Units (RTUs) and Programmable Logic Controllers (PLCs). The control server also usually provides an interface/network service to connect with an HMI.',
            example: 'SCADA Server, MTU, Distributed/Energy Management System'
        },
        {
          index: 2,
            assetType: 'dataGateway',
            assetName: 'Data Gateway',
            description: 'Data Gateway is a device that supports the communication and exchange of data between different systems, networks, or protocols within the ICS. Different types of data gateways are used to perform various functions, including: Protocol Translators, Media Converters, and Data Aggregators. Data gateways are often critical to the forwarding/transmission of critical control or monitoring data within the ICS. Further, these devices often have remote various network services that are used to communicate across different zones or networks.',
            example: 'Data Acquisition Server, Serial to Ethernet Gateway'
        },
        {
          index: 3,
            assetType: 'dataHistorian',
            assetName: 'Data Historian',
            description: 'Data historians, or historian, are systems used to collect and store data, including telemetry, events, alerts, and alarms about the operational process and supporting devices. The historian typically utilizes a database to store this data, and commonly provide tools and interfaces to support the analysis of the data. Data historians are often used to support various engineering or business analysis functions and therefore commonly needs access from the corporate network.',
            example: 'GE Proficy, Ignition Historian'
        },
        {
          index: 4,
            assetType: 'fieldIO',
            assetName: 'Field I/O',
            description: 'Field I/O are devices that communicate with a controller or data aggregator to either send input data or receive output data. Input data may include readings about a given environment/device state from sensors, while output data may include data sent back to actuators for them to either undertake actions or change parameter values. These devices are frequently embedded devices running on lightweight embedded operating systems or RTOSes.',
            example: 'Smart Sensors, Variable Frequency Drives'
        },
        {
          index: 5,
            assetType: 'humanMachineInterface', 
            assetName: 'Human Machine Interface',
            description: 'Human-Machine Interfaces (HMIs) are systems used by an operator to monitor the real-time status of an operational process and to perform necessary control functions, including the adjustment of device parameters. An HMI can take various forms, including a dedicated screen or control panel integrated with a specific device/controller, or a customizable software GUI application.',
            example: 'Operator Workstations, Consoles, or Mounted Touchscreens'
        },
        {
          index: 6,
            assetType: 'intelligentElectronicDevice', 
            assetName: 'Intelligent Electronic Device',
            description: 'An Intelligent Electronic Device (IED) is a type of specialized field device that is designed to perform specific operational functions, frequently for protection, monitoring, or control within the electric sector. IEDs are typically used to both acquire telemetry and execute tailored control algorithms/actions based on customizable parameters/settings. An IED is usually implemented as a dedicated embedded device and supports various network automation protocols to communicate with RTUs and Control Servers.',
            example: 'Protection Relay, Field Device Controller'
        },
        {
          index: 7,
            assetType: 'jumpHost',
            assetName: 'Jump Host',
            description: 'Jump hosts are devices used to support remote management sessions into ICS networks or devices. The system is used to access the ICS environment securely from external networks, such as the corporate network. The user must first remote into the jump host before they can access ICS devices. The jump host may be a customized Windows server using common remote access protocols (e.g., RDP) or a dedicated access management device. The jump host typically performs various security functions to ensure the authenticity of remote sessions, including authentication, enforcing access controls/permissions, and auditing all access attempts.',
            example: 'Intermediate System, Bastion Host, or Gateway'
        },
        {
          index: 8,
            assetType: 'programmableLogicController',
            assetName: 'Programmable Logic Controller',
            description: 'A Programmable Logic Controller (PLC) is an embedded programmable control device. PLCs typically utilize a modular architecture with separate modules used to support its processing capabilities, communication mediums, and I/O interfaces. PLCs allow for the deployment of customized programs/logic to control or monitor an operational process.',
            example:'Process Automation Controller, Field Device Controller'
        }, 
        {
          index: 9,
            assetType: 'remoteTerminalUnit', 
            assetName: 'Remote Terminal Unit',
            description: 'A Remote Terminal Unit (RTU) is a device that typically resides between field devices (e.g., PLCs, IEDs) and control/SCADA servers and supports various communication interfacing and data aggregation functions. RTUs are typically responsible for forwarding commands from the control server and the collection of telemetry, events, and alerts from the field devices.',
            example: 'Motorola ACE'
        },
        {
          index: 10,
            assetType: 'router',
            assetName: 'Router',
            description: 'A computer that is a gateway between two networks at OSI layer 3 and that relays and directs data packets through that inter-network. The most common form of router operates on IP packets.',
            example: 'Cisco, Ubiquiti, TP-Link, NetGear'
        },
        {
          index: 11,
            assetType: 'safetyController',
            assetName: 'Safety Controller',
            description: 'Safety controllers are typically a type of field device used to perform the safety critical function. Safety controllers often support the deployment of custom programs/logic, similar to a PLC, but can also be tailored for sector specific functions/applications.',
            example: 'Safety Instrumented System (SIS) Contoller, Emergency Shutdown Systems (EMS) Controller, Burner Management Systesm (BMS) Controller'
        },
        {
          index: 12,
            assetType: 'virtualPrivateNetworkServer', 
            assetName: 'VPN Server',
            description: 'A VPN server is a device that is used to establish a secure network tunnel between itself and other remote VPN devices, including field VPNs. VPN servers can be used to establish a secure connection with a single remote device, or to securely bridge all traffic between two separate networks together by encapsulating all data between those networks.',
            example: 'Virtual Private Network (VPN) Terminator, Field VPN, Cisco AnyConnect, Ivanti'
        },
        {
          index: 13,
            assetType: 'workstation', 
            assetName: 'Workstation',
            description: 'Workstations are devices used by human operators or engineers to perform various configuration, programming, maintenance, diagnostic, or operational tasks. Workstations typically utilize standard desktop or laptop hardware and operating systems (e.g., MS Windows), but run dedicated control system applications or diagnostic/management software to support interfacing with the control servers or field devices.',
            example: 'Transient Cyber Asset (TCA), Engineering Workstation (EWS), Windows 11 Laptop or Desktop, Linux Laptop or Desktop'
        }
];


const CriticalAssets = ({activeStep, setActiveStep}) =>  {
  const [rows, setRows] = React.useState();
  const [rowModesModel, setRowModesModel] = React.useState({});
  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();

  const date = new Date();
  const currentYear = date.getFullYear();

  const [assetToDelete, setAssetToDelete] = useState(null);
  const [criticalAssetName, setCriticalAssetName] = useState(null);

  const [open, setOpen] = useState(false);
  const [userAddedCATemp , setUserAddedCATemp] = useState(0);
  const [openInstructions, setOpenInstructions] = useState(true);

  const {assessmentID} = useParams();
  const [refresh, setRefresh] = useState(true);
  // const [assessmentID2, userId] = useContext(AssessmentContext);


   // Get Critical Assets Function.  This should pull cyberAssets for the current assessment. 
const getCriticalAssets = async () => {
  try {
    const response = await axiosPrivate.get(`/criticalassets/criticalassets/${assessmentID}`, {
        withCredentials: true
    });
    const data = await response.data;
    setRows(data);
    // console.log(data);
  } catch (err) {
    // alert("Login Expired.");
    console.error(err);
    // navigate('/login', { state: { from: location }, replace: true });
  }
};

  useEffect(() => {
      getCriticalAssets();
    }, [refresh]);  // eslint-disable-line react-hooks/exhaustive-deps
    if (!rows) {
      return null;
    }


    //   console.log("USER ID AND ASSESSMENT ID:");
    // console.log(assessmentID2);
    // console.log(userId);

  
  // Update Database Function 
  const handleAddCyberAsset = () => {
    setRows(null);
  }

  const handleRowEditStop = (id, event) => {
    if (id.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (row) => () => {
    setRowModesModel({ ...rowModesModel, [row._id]: { mode: GridRowModes.Edit } });
  };

  // Update Database Function 
  const handleCyberAssetUpdate = async (updatedRow) => {
    let id = updatedRow._id;
    let assetName = updatedRow.assetName;
    let assetType = updatedRow.assetType;
    let description = updatedRow.description;
    let riskManagementDecision = updatedRow.riskManagementDecision;
    const cyberAssetData = JSON.stringify({  description, riskManagementDecision });
    try {
      const response = await axiosPrivate.patch(`/criticalassets/criticalasset/${id}`,
        JSON.stringify({ assessmentID, assetName, assetType, description, riskManagementDecision }),
        {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
        }
        );
        const data = await response.data;
        // Axios provides responses in JSON by default. The response JSON is always named 'data'. 
        // The console log commands below allow you to see the responses from Axios for the response.  
        // navigate(from, { replace: true });
      } catch (err) {
        if (!err?.response) {
            console.log('No Server Response');
        } else {
            console.log('Action Failed')
        }
      }
    };

  const handleSaveClick = (row) => {
    setRowModesModel({ ...rowModesModel, [row._id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row._id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row._id === newRow._id ? updatedRow : row)));
    handleCyberAssetUpdate(updatedRow);
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleDeleteAsset = async () => {
    try {
        const response = await axiosPrivate.delete(`/criticalassets/${assetToDelete}`, 
            ({ "active": false }),
          {
              headers: { 'Content-Type': 'application/json' },
              withCredentials: true
          }
      );
        if (response.data.success) {
            alert(response.data.msg);
        } 
        // console.log(response);
    } catch (err) {
        console.error(err);
    }
     setAssetToDelete(null);
     setCriticalAssetName(null);
     setRefresh(!refresh);
 }
 
 const handleClickOpen = (params) => {
    setAssetToDelete(params.row._id);
    setCriticalAssetName(params.row.assetName || "this device");
    setOpen(true);
  };

const handleNo = () => {
  setOpen(false);
};

const handleYes = () => {
    handleDeleteAsset();
    setOpen(false);
};

const columns = [
  {
      field: "assetName",
      headerName: "Name",
      flex: 1,
      editable: true
  },
  {
      field: "assetType",
      headerName: "Asset Type",
      flex: 1,
      editable: true
  },
  {
      field: "assetDescription",
      headerName: "Description",
      flex: 2,
      editable: true
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 250,
    cellClassName: 'actions',
    getActions: (params) => {
      const isInEditMode = rowModesModel[params.row._id]?.mode === GridRowModes.Edit;

      if (isInEditMode) {
        return [
          <GridActionsCellItem
            icon={<SaveIcon />}
            label="Save"
            sx={{
              color: 'primary.main',
            }}
            onClick={() => handleSaveClick(params.row)}
          />,
          <GridActionsCellItem
            icon={<CancelIcon />}
            label="Cancel"
            className="textPrimary"
            onClick={handleCancelClick(params.row._id)}
            color="inherit"
          />,
        ];
      }

      return [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          className="textPrimary"
          onClick={handleEditClick(params.row)}
          color="inherit"
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleClickOpen(params)}
          color="inherit"
        />,
      ];
    },
  },
];

const handleAddNewCriticalAsset = async () => {
    let assetName = DefaultCriticalAssets[userAddedCATemp]?.assetName;
    let riskManagementDecision = "";
    let assetType = DefaultCriticalAssets[userAddedCATemp]?.assetType;
    let assetDescription = DefaultCriticalAssets[userAddedCATemp]?.description || "";
    let active = true;
    try {
      const response = await axiosPrivate.post(`/criticalassets/criticalasset/new`,
        JSON.stringify({ assessmentID, assetName, assetType, assetDescription, riskManagementDecision, active }),
      {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
      } 
    );
    } catch (err) {
        console.log(err);
        
    }
  setUserAddedCATemp("");
  setRefresh(!refresh);
};
 


const handleOpenInstructions = () => {
  setOpenInstructions(!openInstructions);
};

const handleNext = () => {
  setActiveStep(activeStep+1)
}


  return (
    <Box sx={{ marginTop:'1em' }}>
      <Header title='Critical Assets' subtitle='Add critical assets.' />
      <Grid container spacing={2}>
        <Grid item size={12} >
        <Paper sx={{ backgroundImage: "none", backgroundColor: theme.palette.background.alt, borderRadius: "0.55rem", mr:'0.55rem', p:'4px'}} >
          <Grid item size={12} sx={{ p:'0.5rem' }}>
          {openInstructions ? (
              <IconButton color="primary"  aria-label="open or close instructions" onClick={handleOpenInstructions} >
                <ExpandCircleDownIcon sx={{ transform: 'rotate(180deg)'}} color='primary' fontSize="large" />
                <Typography variant='h3' sx={{ ml: '1.5rem' }}><u>Instructions</u>:</Typography>
              </IconButton>
                ) : (
                <IconButton color="primary"  aria-label="open or close detection and response" onClick={handleOpenInstructions} >
                  <ExpandCircleDownIcon color='primary' fontSize="large" />
                  <Typography variant='h3' sx={{ ml: '1.5rem' }}>Instructions</Typography>
                </IconButton>
                )}
              <Divider sx={{ border:0.5, mt:'6px' }}/>
          </Grid>
          <Collapse in={openInstructions} timeout="auto" unmountOnExit>
            <Grid container spacing={1}>
              <Grid item size={12} sx={{ ml:'2rem' }}>
                <Typography variant='h3'><b>RECOMMENDATION:</b>  If this is your first time using this tool you should only add your most critical assets.</Typography>
                <Typography>Add critical assets from the drop-down or define your own. Click on the Click on the <DeleteIcon sx={{ cursor:'text' }}  /> to remove an asset. Click on the <EditIcon sx={{ cursor:'text' }}  /> to make changes to the default critical assets. </Typography>
                <Typography>Enter the name of the restricted area and press the <AddCircleIcon color="primary" /> to add a new critical asset.</Typography>
                <Typography>This information is necessary to complete the secuirty assessment.</Typography>
                <Typography>This information is included in the assessment report.</Typography>
              </Grid>
            </Grid>
          </Collapse>
          </Paper>
          <Paper sx={{ backgroundImage: "none", backgroundColor: theme.palette.background.alt, borderRadius: "0.55rem", mr:'0.55rem', mt:'1rem', p:'4px', minHeight:'250px'}} >
              <Grid item size={12} >
              <Typography display="inline" sx={{ fontSize:'1.5em', marginTop:'0.5em', p:'0.5em'  }}>Critical Assets:</Typography>
                <input style={{ height: '40px', width: '20%', marginLeft: '2rem' }}
                  type="text"
                  id="userAddedCATemp"
                  onChange={(e) => setUserAddedCATemp(e.target.value)}
                />
                <select style={{ height: '40px', width: '20%', marginLeft: '2rem'}} onChange={(e) => setUserAddedCATemp(e.target.value)}> 
                  {DefaultCriticalAssets.map((asset) => (
                    <option value={asset.index}>{asset.assetName}</option>
                  ))}
               </select> 
                <IconButton color="primary"  aria-label="add a critical asset" onClick={handleAddNewCriticalAsset}>
                  <AddCircleIcon fontSize="large" />
                </IconButton>
                <Button sx={{ ml:'5rem', mr:'5%'}} variant="contained" onClick={handleNext}>Save & Continue</Button>
              </Grid>

              <Grid item size={12} sx={{ width: '99%' }}>
                <Dialog
                  open={open}
                  onClose={handleNo}
                  aria-labelledby="alert-dialog-title"
                  aria-describedby="alert-dialog-description"
                  >
                  <DialogTitle id="alert-dialog-title">
                  {"Verify Delete Command"}
                  </DialogTitle>
                  <DialogContent>
                  <DialogContentText id="alert-dialog-description">
                      Are you sure you want to delete {criticalAssetName}?
                  </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                  <Button onClick={handleYes}>Yes</Button>
                  <Button onClick={handleNo} autoFocus>
                      No
                  </Button>
                  </DialogActions>
                </Dialog>
                <Box
                  sx={{
                    width: '100%',
                    fontSize: '2em',
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: theme.palette.secondary.alt,
                        color: theme.palette.secondary[100],
                        borderBottom: "solid 2px black",
                        borderTop: "solid 2px black",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: theme.palette.primary.light,
                    },
                    "& .MuiDataGrid-footerContainer": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary[100],
                        borderTop: "none"
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${theme.palette.secondary[200]} !important`,
                    },
                    // "& .MuiDataGrid-main": {
                    //   fontSize: "1.5em"
                    // }
                     "& .MuiDataGrid-root .MuiDataGrid-cell": {
                            whiteSpace: "normal !important",
                            wordWrap: "break-word !important",
                            lineHeight: "1.5",
                            },
                        "& .even": {
                                // backgroundColor: theme.palette.neutral.rowEven,
                                backgroundColor: theme.palette.primary.rowEven,
                            },
                            "& .odd": {
                                // backgroundColor: theme.palette.neutral.rowOdd,
                                backgroundColor: theme.palette.primary.rowOdd,
                            },
                  }}
                  >
                  <DataGrid
                    rows={rows || []} 
                    getRowId={(row) => row._id}
                    getRowHeight={() => 'auto'}
                    columns={columns}
                    getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                      }
                    editMode="row"
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={handleRowModesModelChange}
                    onRowEditStop={handleRowEditStop}
                    processRowUpdate={processRowUpdate}
                    slots={{
                      toolbar: DataGridCustomToolbar,
                    }}
                    slotProps={{
                      toolbar: { setRows, setRowModesModel },
                    }}
                  />
                </Box>
                
              </Grid>
        </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CriticalAssets
